import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, AlertTriangle, Truck, Brain, CheckCircle } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const typeConfig = {
  critical_ticket: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  triage_alert: { icon: Brain, color: 'text-amber-500', bg: 'bg-amber-50' },
  dispatch: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-50' },
  status_change: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  triage_override: { icon: Brain, color: 'text-purple-500', bg: 'bg-purple-50' },
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications`, { credentials: 'include' });
      if (res.ok) setNotifications(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    // WebSocket connection
    const connect = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/ws/notifications`);
        ws.onmessage = (event) => {
          const notif = JSON.parse(event.data);
          setNotifications(prev => [notif, ...prev]);
        };
        ws.onclose = () => setTimeout(connect, 5000);
        wsRef.current = ws;
      } catch {}
    };
    connect();
    return () => wsRef.current?.close();
  }, [fetchNotifications]);

  const unread = notifications.filter(n => !n.read).length;

  const markAllRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read-all`, { method: 'PUT', credentials: 'include' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <div className="relative">
      <button data-testid="notification-bell" onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
        <Bell className="w-5 h-5 text-gray-500" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-50 w-96 bg-white rounded-xl border border-gray-200 shadow-xl max-h-[480px] overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-ibm font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={markAllRead} className="text-xs font-ibm text-blue-600 hover:text-blue-700" data-testid="mark-all-read-btn">Mark all read</button>
                )}
                <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-gray-400" /></button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[400px]">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400 font-ibm">No notifications yet</div>
              ) : (
                notifications.slice(0, 20).map(n => {
                  const config = typeConfig[n.type] || typeConfig.status_change;
                  const Icon = config.icon;
                  return (
                    <div key={n.id} data-testid={`notif-${n.id}`} className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-blue-50/30' : ''}`}>
                      <div className="flex gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-ibm text-gray-900">{n.data?.message || n.type}</p>
                          <p className="text-xs font-ibm-mono text-gray-400 mt-0.5">{new Date(n.timestamp).toLocaleString('de-DE')}</p>
                        </div>
                        {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
